/**
 * Page thumbnail panel: select, delete, rotate, and drag to reorder.
 *
 * Thumbnails are rendered client-side from the same pdf.js document the main
 * view uses, so they always show the document's true current state -- including
 * anything Odie has placed on it -- without a second round trip.
 */

import { useEffect, useRef, useState } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import type { PageInfo } from '../api'

interface Props {
  /** What the header shows in place of "Pages" (the panel's tabs). */
  title?: React.ReactNode
  doc: PDFDocumentProxy
  pages: PageInfo[]
  current: number
  selected: Set<number>
  onGoTo: (index: number) => void
  onToggleSelect: (index: number, additive: boolean) => void
  onReorder: (order: number[]) => void
  onDelete: (indices: number[]) => void
  onRotate: (indices: number[], degrees: number) => void
  onOcr: (index: number) => void
  ocrPages: Set<number>
}

export function Thumbnails({
  title,
  doc,
  pages,
  current,
  selected,
  onGoTo,
  onToggleSelect,
  onReorder,
  onDelete,
  onRotate,
  onOcr,
  ocrPages,
}: Props) {
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  const activeSelection = selected.size ? [...selected] : [current]

  const handleDrop = (to: number) => {
    setDragOver(null)
    const from = dragFrom
    setDragFrom(null)
    if (from === null || from === to) return

    // Build the new order by lifting the dragged page and reinserting it.
    const order = pages.map((p) => p.index)
    const [moved] = order.splice(from, 1)
    order.splice(to, 0, moved)
    onReorder(order)
  }

  return (
    <aside className="thumbs" aria-label="Pages">
      <div className="thumbs__header">
        {title ?? <span>Pages</span>}
        <div className="thumbs__actions">
          <button
            className="iconbtn"
            title="Rotate selected 90° clockwise"
            onClick={() => onRotate(activeSelection, 90)}
          >
            ↻
          </button>
          <button
            className="iconbtn iconbtn--danger"
            title={
              pages.length <= activeSelection.length
                ? 'A document must keep at least one page'
                : `Delete ${activeSelection.length} page(s)`
            }
            disabled={pages.length <= activeSelection.length}
            onClick={() => onDelete(activeSelection)}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="thumbs__list">
        {pages.map((page, position) => (
          <div
            key={page.index}
            className="thumb"
            data-current={page.index === current}
            data-selected={selected.has(page.index)}
            data-dragover={dragOver === position}
            draggable
            onDragStart={() => setDragFrom(position)}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(position)
            }}
            onDragLeave={() => setDragOver((v) => (v === position ? null : v))}
            onDrop={() => handleDrop(position)}
            onDragEnd={() => {
              setDragFrom(null)
              setDragOver(null)
            }}
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey || e.shiftKey) {
                onToggleSelect(page.index, true)
              } else {
                onGoTo(page.index)
                onToggleSelect(page.index, false)
              }
            }}
          >
            <ThumbCanvas doc={doc} pageIndex={page.index} rotation={page.rotation} />
            <div className="thumb__label">
              {page.label}
              {!page.hasText && (
                <button
                  className="thumb__ocr"
                  title={
                    ocrPages.has(page.index)
                      ? 'Text layer added by OCR'
                      : 'No text on this page — run OCR to make it searchable'
                  }
                  data-done={ocrPages.has(page.index)}
                  onClick={(e) => {
                    e.stopPropagation()
                    onOcr(page.index)
                  }}
                >
                  {ocrPages.has(page.index) ? 'OCR ✓' : 'OCR'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </aside>
  )
}

function ThumbCanvas({
  doc,
  pageIndex,
  rotation,
}: {
  doc: PDFDocumentProxy
  pageIndex: number
  rotation: number
}) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    let cancelled = false
    let task: { cancel: () => void } | null = null

    void (async () => {
      const page = await doc.getPage(pageIndex + 1)
      if (cancelled) return
      const canvas = ref.current
      if (!canvas) return

      // Fit the thumbnail column, at device resolution so it is not blurry.
      const target = 132
      const base = page.getViewport({ scale: 1 })
      const dpr = window.devicePixelRatio || 1
      const viewport = page.getViewport({ scale: (target / base.width) * dpr })

      const context = canvas.getContext('2d')
      if (!context) return
      canvas.width = Math.floor(viewport.width)
      canvas.height = Math.floor(viewport.height)
      canvas.style.width = `${target}px`
      canvas.style.height = `${Math.floor(viewport.height / dpr)}px`

      const render = page.render({ canvasContext: context, viewport })
      task = render
      try {
        await render.promise
      } catch (err) {
        if ((err as Error)?.name !== 'RenderingCancelledException') throw err
      }
    })()

    return () => {
      cancelled = true
      task?.cancel()
    }
    // `rotation` participates so a rotate re-renders the thumbnail.
  }, [doc, pageIndex, rotation])

  return <canvas ref={ref} className="thumb__canvas" />
}
